import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { theme, Typography } from 'antd'
import { Dayjs } from 'dayjs'
import capitalize from 'lodash/capitalize'
import sortBy from 'lodash/sortBy'
import { DateRangeFilter, DateRangeValue } from '../../design-system/DateRangeFilter'
import { FilterTrigger, FilterTriggerRow, FilterRowItem, FilterValues, useFilterRowLabels, buildLabelsFilterRowItem } from '../../design-system/FilterTrigger'
import { toDepartmentFilterOptions } from '@vacationtracker/shared/functions/department-options'
import { locationOptionsByCountry } from '@vacationtracker/shared/functions/location-country-groups'
import { DateFormat, Display, ReportPeriod } from '@vacationtracker/shared/types/export'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { useShouldEnableFeatures } from '../../store/use-should-enable-features'
import { IExportFiltersResponse } from './types'

const { Text } = Typography

const DISPLAYS: { value: Display; labelId: string }[] = [
  { value: 'days', labelId: 'app.days' },
  { value: 'hours', labelId: 'app.hours' },
]
const DATE_FORMATS: DateFormat[] = ['YYYY-MM-DD', 'MM/DD/YYYY']
const BALANCE_PERIODS: { value: ReportPeriod; labelId: string }[] = [
  { value: 'CURRENT_YEAR', labelId: 'export.currentYear' },
  { value: 'LAST_MONTH', labelId: 'export.lastMonth' },
  { value: 'PREVIOUS_YEAR', labelId: 'app.previousYear' },
  { value: 'NEXT_YEAR', labelId: 'export.nextYear' },
  { value: 'YEAR_TO_DATE', labelId: 'export.yearToDate' },
  { value: 'CUSTOM_DAYS', labelId: 'app.customDays' },
]
const REQUEST_TYPES = ['LEAVE', 'TOIL']
const REQUEST_STATUSES = ['APPROVED', 'CANCELLED', 'DENIED', 'EXPIRED', 'OPEN']
const USER_STATUSES = ['ACTIVE', 'INACTIVE', 'DELETED']

const enumOptions = (values: string[]) => values.map((value) => ({ value, label: value === 'TOIL' ? value : capitalize(value) }))

function ControlsRow({ label, children }: { label: string; children: React.ReactNode }) {
  const { token } = theme.useToken()
  return (
    <div role="group" aria-label={label} style={{ display: 'flex', alignItems: 'center', gap: token.paddingSM, flexWrap: 'wrap', marginBottom: token.marginSM }}>
      <Text type="secondary" style={{ minWidth: 64 }}>
        {label}
      </Text>
      <div style={{ display: 'flex', alignItems: 'center', gap: token.paddingXS, flexWrap: 'wrap', flex: 1 }}>{children}</div>
    </div>
  )
}

export interface IValueControlProps<T> {
  value: T
  onChange: (value: T) => void
}

interface IShowTriggerProps<T extends string> extends IValueControlProps<T> {
  id: string
  labelId: string
  options: { value: T; label: string }[]
}

/** A Show-row setting reads like the filters beside it ("Display: days"), but always holds exactly one value. */
function ShowTrigger<T extends string>({ id, labelId, options, value, onChange }: IShowTriggerProps<T>) {
  const { formatMessage } = useIntl()
  return (
    <FilterTrigger
      config={{ id, label: formatMessage({ id: labelId }), options, multiple: false, keepOrder: true }}
      value={[value]}
      onChange={(next) => {
        const picked = options.find((option) => option.value === next[0])
        if (picked) onChange(picked.value)
      }}
      clearable={false}
    />
  )
}

export function DisplayToggle({ value, onChange }: IValueControlProps<Display>) {
  const { formatMessage } = useIntl()
  const options = DISPLAYS.map((display) => ({ value: display.value, label: formatMessage({ id: display.labelId }) }))
  return <ShowTrigger id="display" labelId="app.display" options={options} value={value} onChange={onChange} />
}

export function DateFormatToggle({ value, onChange }: IValueControlProps<DateFormat>) {
  const options = DATE_FORMATS.map((format) => ({ value: format, label: format }))
  return <ShowTrigger id="dateFormat" labelId="app.dateFormat" options={options} value={value} onChange={onChange} />
}

export interface IReportPeriodRangeProps {
  defaultDates: [Dayjs, Dayjs]
  format: DateFormat
  onChange: (dates: [Dayjs, Dayjs]) => void
}

/** Owns its range so a remount (switching back to Custom days) starts from the defaults again, as before. */
export function ReportPeriodRange({ defaultDates, format, onChange }: IReportPeriodRangeProps) {
  const { formatMessage } = useIntl()
  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultDates)

  const handleChange = (next: DateRangeValue) => {
    if (!next?.[0] || !next[1]) return
    const dates: [Dayjs, Dayjs] = [next[0], next[1]]
    setRange(dates)
    onChange(dates)
  }

  return (
    <div role="group" aria-label={formatMessage({ id: 'automations.period' })}>
      <DateRangeFilter value={range} onChange={handleChange} clearable={false} format={format} />
    </div>
  )
}

export function BalancePeriodSelect({ value, onChange }: IValueControlProps<ReportPeriod>) {
  const { formatMessage } = useIntl()
  const options = BALANCE_PERIODS.map((period) => ({ value: period.value, label: formatMessage({ id: period.labelId }) }))
  return <ShowTrigger id="period" labelId="automations.period" options={options} value={value} onChange={onChange} />
}

export interface IReportControlsProps {
  show: React.ReactNode
  companyData: IExportFiltersResponse
  defaultFilters: FilterValues
  onFiltersChange: (filters: FilterValues) => void
  /** Leave History also filters by request type and request status. */
  includeRequestFilters?: boolean
}

export function ReportControls(props: IReportControlsProps) {
  const { show, companyData, defaultFilters, onFiltersChange, includeRequestFilters } = props
  const { formatMessage } = useIntl()
  const rowLabels = useFilterRowLabels()
  const labelsEnabled = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.labels)
  const [values, setValues] = useState<FilterValues>(defaultFilters)

  // The page may rewrite the map it receives (clearUserIdsOnStatusChange empties userIds), so the
  // row reads it back after the call rather than keeping a stale selection.
  const emit = (next: FilterValues) => {
    onFiltersChange(next)
    setValues({ ...next })
  }

  const clearLabel = (label: string) => formatMessage({ id: 'users.table.clearFilter' }, { filter: label })
  const item = (id: string, labelId: string, options: FilterRowItem['options']): FilterRowItem => {
    const label = formatMessage({ id: labelId })
    return { id, label, options, clearLabel: clearLabel(label) }
  }
  const toOptions = (items: { id: string; name: string }[]) => items.map((entry) => ({ value: entry.id, label: entry.name }))

  const locationsByCountry = locationOptionsByCountry(companyData.getLocationList, formatMessage({ id: 'locationPlace.card.noCountrySet' }))

  const configs: FilterRowItem[] = [
    { ...item('locationIds', 'app.locations', locationsByCountry ?? toOptions(companyData.getLocationList)), keepOrder: Boolean(locationsByCountry) },
    item('departmentIds', 'app.departments', toDepartmentFilterOptions(companyData.getTeamList)),
    {
      ...buildLabelsFilterRowItem({ labels: companyData.getLabels, labelsEnabled, formatMessage, clearLabel: clearLabel(formatMessage({ id: 'app.labels' })) }),
      id: 'labelIds',
    },
    { ...item('userIds', 'app.users', toOptions(companyData.getUsersFiltered.users)), searchable: true },
    {
      ...item(
        'leaveTypeIds',
        'app.leaveTypes',
        sortBy(companyData.getLeaveTypeList, 'position').map((leaveType) => ({ value: leaveType.id, label: leaveType.name, swatch: leaveType.color }))
      ),
      keepOrder: true,
    },
    ...(includeRequestFilters ? [item('requestTypes', 'app.requestType', enumOptions(REQUEST_TYPES)), item('statuses', 'app.status', enumOptions(REQUEST_STATUSES))] : []),
    item('userStatuses', 'app.userStatus', enumOptions(USER_STATUSES)),
  ]

  return (
    <div className="report-controls">
      <ControlsRow label={formatMessage({ id: 'app.show' })}>{show}</ControlsRow>
      <ControlsRow label={formatMessage({ id: 'app.filters' })}>
        <FilterTriggerRow configs={configs} values={values} onChange={emit} visibleLimit={configs.length} collapseOnMobile={false} onClearAll={() => emit({})} labels={rowLabels} />
      </ControlsRow>
    </div>
  )
}

export default ReportControls
